import type { MovementMeasureType } from "./movementRegistry";

/**
 * Canonical, deterministic display formatters for a movement's logged value,
 * keyed on its `measureType` (the orthogonal unit) — NOT `scoringType` (the
 * scoring shape, `higherIsBetter | lowerIsBetter | binary`).
 *
 * Why these helpers live here, in `@lemonadetraining/types`:
 * Mobile and web previously maintained parallel formatters that drifted on
 * edge cases (zero/negative duration sentinel, kg/reps rounding, thousands
 * grouping). One canonical contract avoids that drift across the two
 * platforms and Cloud Functions.
 *
 * Why no i18n / no `useTranslation` here:
 * The package is pure runtime, with no React, no i18next, and no Intl/ICU
 * dependency. Both platforms' i18n wrappers (`movements.stat.kg`, etc.) call
 * these to produce the numeric/unit portion, then apply locale around it.
 * Thousands grouping is hand-rolled (rather than `Number.prototype.toLocaleString`)
 * because jest's default Node runtime is built without full ICU data, so
 * `(1250).toLocaleString("en-US")` returns "1250" there — formatters must be
 * deterministic across environments.
 *
 * Display conventions:
 * - `kg`       → "120 kg" / "72.5 kg" (two decimals; athletes log half-kilos)
 * - `reps`     → "12 reps"          (Math.round)
 * - `time`     → "3:21" (m:ss) or "1:02:03" (h:mm:ss for >= 1h)
 * - `calories` → "35 cal"           (Math.round)
 * - `distance` → "1,250 m"          (metres, thousands-separated, no rounding)
 * - `binary`   → "Unlocked" / "Not yet"
 */

/**
 * Format a duration in seconds as `m:ss`, rolling over to `h:mm:ss` at one
 * hour or more. Seconds are always two-digit zero-padded; minutes are
 * zero-padded only inside the hour-plus form. Non-finite or non-positive
 * input clamps to `"0:00"` ("no time logged" is a real state for the athlete
 * UI; throwing or returning an em-dash would force every call site to
 * special-case it).
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const ss = String(secs).padStart(2, "0");
  if (hours > 0) {
    const mm = String(minutes).padStart(2, "0");
    return `${hours}:${mm}:${ss}`;
  }
  return `${minutes}:${ss}`;
}

/** A duration split into whole minutes and the remaining 0–59 seconds. */
export type DurationParts = { minutes: number; seconds: number };

/**
 * Split a total seconds count into whole minutes + remaining seconds — the
 * inverse of {@link durationPartsToSeconds}, for seeding a mm:ss picker from a
 * stored value. Mirrors {@link formatDuration}: non-finite or non-positive
 * input clamps to `{ minutes: 0, seconds: 0 }` rather than throwing.
 */
export function secondsToDurationParts(totalSeconds: number): DurationParts {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return { minutes: 0, seconds: 0 };
  }
  const total = Math.round(totalSeconds);
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

/**
 * A duration split into whole hours, the remaining 0–59 minutes, and the
 * remaining 0–59 seconds.
 */
export type HmsParts = { hours: number; minutes: number; seconds: number };

/**
 * Split a total seconds count into whole hours + remaining minutes + remaining
 * seconds — the hours-aware inverse of {@link formatDuration}, for seeding an
 * h:mm:ss picker from a stored value. Use this (rather than
 * {@link secondsToDurationParts}) whenever the value can reach one hour or more,
 * so endurance times are never silently truncated when round-tripped through the
 * input UI. Reverse with {@link hmsPartsToSeconds}.
 *
 * Decomposes identically to {@link formatDuration} (round, then hours / minutes
 * / seconds). Mirrors the sibling helpers: non-finite or non-positive input
 * clamps to `{ hours: 0, minutes: 0, seconds: 0 }` rather than throwing.
 */
export function secondsToHmsParts(totalSeconds: number): HmsParts {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }
  const total = Math.round(totalSeconds);
  return {
    hours: Math.floor(total / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

/**
 * Combine whole hours + minutes + seconds into the canonical total-seconds unit
 * used on the wire and in storage — the explicit inverse of
 * {@link secondsToHmsParts}, for an h:mm:ss picker to report its value. Pairs
 * with {@link secondsToHmsParts} exactly as {@link durationPartsToSeconds} pairs
 * with {@link secondsToDurationParts}. Non-finite or negative parts are treated
 * as zero and fractional parts are floored. Parts are NOT capped (a caller
 * passing `minutes: 90` gets the literal carry), so an h:mm:ss picker should
 * constrain its minutes/seconds columns to 0–59 and its hours column to a
 * sensible ceiling.
 */
export function hmsPartsToSeconds(
  hours: number,
  minutes: number,
  seconds: number,
): number {
  const h = Number.isFinite(hours) && hours > 0 ? Math.floor(hours) : 0;
  const m = Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0;
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return h * 3600 + m * 60 + s;
}

/**
 * Combine minutes + seconds into the canonical total-seconds unit used on the
 * wire and in storage (see {@link formatDuration}) — the inverse of
 * {@link secondsToDurationParts}. Non-finite or negative parts are treated as
 * zero and fractional parts are floored. Seconds are NOT capped at 59 (callers
 * passing `{ minutes: 0, seconds: 90 }` get 90), so a mm:ss picker should
 * constrain its seconds column to 0–59.
 */
export function durationPartsToSeconds(
  minutes: number,
  seconds: number,
): number {
  const m = Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0;
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return m * 60 + s;
}

/**
 * Group an integer with comma thousands separators, preserving sign and any
 * fractional part. Hand-rolled to stay deterministic on jest's no-ICU Node
 * runtime (see file header).
 */
function withThousands(n: number): string {
  const negative = n < 0;
  const [intPart, fracPart] = Math.abs(n).toString().split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}${fracPart ? `.${fracPart}` : ""}`;
}

/**
 * Full human-readable value for a measure unit, including the unit token.
 *
 * - kg keeps two decimals (a logged 72.5 must not read back as 73);
 *   reps/calories are rounded to integers (athletes don't log "12.7 reps").
 * - `time` delegates to {@link formatDuration} and omits the unit (the m:ss
 *   format IS the unit).
 * - `distance` is grouped but not rounded — fractional metres are preserved
 *   for the rare logged-as-decimal case.
 * - `binary` treats any value `> 0` as "Unlocked".
 */
export function formatMeasureValue(
  value: number,
  measureType: MovementMeasureType,
): string {
  switch (measureType) {
    case "kg":
      return `${Math.round(value * 100) / 100} kg`;
    case "reps":
      return `${Math.round(value)} reps`;
    case "time":
      return formatDuration(value);
    case "calories":
      return `${Math.round(value)} cal`;
    case "distance":
      return `${withThousands(value)} m`;
    case "binary":
      return value > 0 ? "Unlocked" : "Not yet";
  }
}

/**
 * Bare unit suffix for compact "current / target" pairs (e.g. "120 / 170 kg").
 * `time` and `binary` return `""` — `time` is pre-formatted as `m:ss` so a
 * trailing unit would be redundant, and `binary` has no numeric unit at all.
 */
export function measureUnitSuffix(measureType: MovementMeasureType): string {
  switch (measureType) {
    case "kg":
      return "kg";
    case "reps":
      return "reps";
    case "calories":
      return "cal";
    case "distance":
      return "m";
    case "time":
    case "binary":
      return "";
  }
}
